/**
 * Regras de remoção do carrinho (/cart) — o que sai junto com um item e a
 * ressincronização de brindes. Compartilhadas com o carrinho da adição
 * facilitada (assets/zrzs-quick-add-cart-panel.js) via @theme/cart-remove-all:
 * mesma lógica, só a atualização da tela muda (/cart recarrega a página; o
 * painel aplica o HTML da Section Rendering API).
 *
 * As keys a remover e os "stack gift checks" de cada item vêm do Liquid
 * (data-item-keys / data-stack-gift-checks — snippets/cart-products.liquid e
 * snippets/zrzs-quick-add-cart-content.liquid).
 */
export const CartRemoval = {
  /**
   * @param {{ itemKeys: string, stackGiftChecks: string }} params
   * @returns {Promise<{ keysToRemove: string[], giftsToAdd: any[] }>}
   */
  async buildPlan({ itemKeys, stackGiftChecks }) {
    if (!itemKeys) throw new Error('Keys dos itens não encontrados');

    const keysToRemove = itemKeys.split(',').map(key => key.trim()).filter(Boolean);

    // Busca o carrinho uma única vez — usado tanto pelo stackGiftChecks
    // quanto pela sincronização de brindes por faixa de valor.
    const cartRes = await fetch('/cart.js');
    const cart = cartRes.ok ? await cartRes.json() : { items: [], total_price: 0 };
    const cartItems = /** @type {any[]} */ (cart.items || []);

    // Nested cart lines: a Shopify já remove os filhos junto com o pai, mas
    // eles entram aqui pra contar no valor removido (faixa de brindes abaixo).
    const parentKeySet = new Set(keysToRemove);
    cartItems.forEach((/** @type {any} */ item) => {
      const parentKey = item.parent_relationship && item.parent_relationship.parent_key;
      if (parentKey && parentKeySet.has(parentKey) && !keysToRemove.includes(item.key)) {
        keysToRemove.push(item.key);
      }
    });

    // --- Lógica legada: remove gifts vinculados a frames elegíveis ---
    if (stackGiftChecks) {
      const removingKeySet = new Set(keysToRemove);

      // Formato: "giftKey1:giftVariantId1,giftKey2:giftVariantId2"
      const giftChecks = stackGiftChecks.split(',').map(s => {
        const colonIdx = s.lastIndexOf(':');
        return {
          key: s.slice(0, colonIdx).trim(),
          variantId: s.slice(colonIdx + 1).trim(),
        };
      });

      for (const { key: giftKey, variantId: giftRuleKey } of giftChecks) {
        const eligibleProp = `_eligibleForGift_${giftRuleKey}`;
        const hasOtherEligible = cartItems.some(
          (/** @type {{ key: string, properties: Record<string, string> }} */ item) =>
            !removingKeySet.has(item.key) &&
            item.properties[eligibleProp] === 'true'
        );
        if (!hasOtherEligible) {
          keysToRemove.push(giftKey);
        }
      }
    }

    // --- Sincronização de brindes gerenciados (regras no sessionStorage) ---
    const giftsToAdd = this._syncManagedGiftsOnRemove(
      cartItems,
      keysToRemove,
      cart.total_price || 0
    );

    return { keysToRemove, giftsToAdd };
  },

  /**
   * Executa todas as remoções em um único update e adiciona os brindes da
   * nova faixa. Com `sections`, devolve o HTML delas (Section Rendering API).
   * @param {{ keysToRemove: string[], giftsToAdd: any[] }} plan
   * @param {{ sections?: string, sectionsUrl?: string }} [options]
   * @returns {Promise<Record<string, string> | undefined>}
   */
  async execute({ keysToRemove, giftsToAdd }, { sections, sectionsUrl } = {}) {
    const sectionsPayload = sections
      ? { sections, sections_url: sectionsUrl || window.location.pathname }
      : {};

    const updates = /** @type {Record<string, number>} */ ({});
    keysToRemove.forEach(key => { updates[key] = 0; });

    const response = await fetch(
      /** @type {any} */ (window.Shopify).routes.root + 'cart/update.js',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates, ...sectionsPayload }),
      }
    );

    if (!response.ok) throw new Error(`Erro ao remover itens: ${response.status}`);
    let { sections: renderedSections } = await response.json();

    // Adiciona brindes da nova faixa (se houver)
    if (giftsToAdd.length > 0) {
      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: giftsToAdd, ...sectionsPayload }),
      });
      if (addResponse.ok && sections) {
        const added = await addResponse.json();
        if (added.sections) renderedSections = added.sections;
      }
    }

    return renderedSections;
  },

  /**
   * @param {any} gift
   * @returns {string}
   */
  _getGiftRuleId(gift) {
    const id = gift?.gift_id ?? gift?.giftId;
    return id != null && id !== '' ? String(id) : '';
  },

  /**
   * @param {any} gift
   * @returns {string}
   */
  _getGiftRuleKey(gift) {
    const ruleId = this._getGiftRuleId(gift);
    return ruleId || `variant:${gift.variant_id}`;
  },

  /**
   * @returns {Array<{ sourceKey: string, gift: any }>}
   */
  _readCartGiftRules() {
    try {
      const raw = sessionStorage.getItem('_zrzs_cart_gift_rules');
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  },

  /**
   * @param {Array<{ sourceKey: string, gift: any }>} entries
   */
  _writeCartGiftRules(entries) {
    try {
      if (!entries.length) {
        sessionStorage.removeItem('_zrzs_cart_gift_rules');
      } else {
        sessionStorage.setItem(
          '_zrzs_cart_gift_rules',
          JSON.stringify(entries)
        );
      }
    } catch (_) {}
  },

  /**
   * @param {any[]} cartItems
   * @param {string[]} keysToRemove
   */
  _removeCartGiftRulesForCartKeys(cartItems, keysToRemove) {
    const sources = new Set();
    for (const item of cartItems) {
      if (!keysToRemove.includes(item.key)) continue;
      const sourceKey = item.properties?._productVariantTitleValue;
      if (sourceKey) sources.add(sourceKey);
    }
    if (!sources.size) return;
    const entries = this._readCartGiftRules().filter(
      e => !sources.has(e.sourceKey)
    );
    this._writeCartGiftRules(entries);
  },

  /**
   * @param {any[]} cartItems
   * @param {Set<string>} excludeKeys
   */
  _pruneCartGiftRules(cartItems, excludeKeys = new Set()) {
    const activeSources = new Set();
    for (const item of cartItems) {
      if (excludeKeys.has(item.key)) continue;
      if (item.properties?._isGift === 'true') continue;
      const sourceKey = item.properties?._productVariantTitleValue;
      if (sourceKey) activeSources.add(sourceKey);
    }
    const entries = this._readCartGiftRules().filter(e =>
      activeSources.has(e.sourceKey)
    );
    this._writeCartGiftRules(entries);
  },

  /**
   * @returns {any[]}
   */
  _getUniqueManagedGiftsFromStorage() {
    const byKey = new Map();
    for (const { gift } of this._readCartGiftRules()) {
      if (!gift?.variant_id || gift.stack_gifts !== false) continue;
      const key = this._getGiftRuleKey(gift);
      if (!byKey.has(key)) byKey.set(key, gift);
    }
    return [...byKey.values()];
  },

  /**
   * @param {any} gift
   * @param {number} total
   * @returns {boolean}
   */
  _isGiftInValueBand(gift, total) {
    if (gift.min_cart_value != null && total < gift.min_cart_value) return false;
    if (gift.max_cart_value != null && total > gift.max_cart_value) return false;
    return true;
  },

  /**
   * @param {any[]} cartItems
   * @param {string[]} keysToRemove
   * @param {number} currentTotal
   * @returns {any[]}
   */
  _syncManagedGiftsOnRemove(cartItems, keysToRemove, currentTotal) {
    this._removeCartGiftRulesForCartKeys(cartItems, keysToRemove);

    const removingKeySet = new Set(keysToRemove);
    this._pruneCartGiftRules(cartItems, removingKeySet);

    const rules = this._getUniqueManagedGiftsFromStorage();
    if (!rules.length) return [];

    const removedTotal = cartItems
      .filter(item => removingKeySet.has(item.key))
      .reduce((sum, item) => sum + (item.final_line_price || 0), 0);
    const projectedTotal = Math.max(0, currentTotal - removedTotal);

    const rulesByKey = new Map(
      rules.map(gift => [this._getGiftRuleKey(gift), gift])
    );

    const managedInCart = cartItems.filter(
      item =>
        item.properties?._isGift === 'true' &&
        item.properties?._stackGifts === 'false' &&
        !removingKeySet.has(item.key)
    );

    managedInCart
      .filter(item => {
        const key =
          item.properties?._giftRuleId || `variant:${item.variant_id}`;
        const gift = rulesByKey.get(String(key));
        if (!gift) return true;
        return !this._isGiftInValueBand(gift, projectedTotal);
      })
      .forEach(item => keysToRemove.push(item.key));

    const updatedRemovingSet = new Set(keysToRemove);
    const remainingNonGiftItems = cartItems.filter(
      item =>
        !updatedRemovingSet.has(item.key) &&
        item.properties?._isGift !== 'true'
    );

    if (remainingNonGiftItems.length === 0) {
      managedInCart.forEach(item => keysToRemove.push(item.key));
      this._writeCartGiftRules([]);
      return [];
    }

    const inCartKeys = new Set();
    for (const item of cartItems) {
      if (updatedRemovingSet.has(item.key)) continue;
      if (
        item.properties?._isGift !== 'true' ||
        item.properties?._stackGifts !== 'false'
      ) {
        continue;
      }
      const ruleId = item.properties?._giftRuleId;
      if (ruleId) inCartKeys.add(String(ruleId));
      else inCartKeys.add(`variant:${item.variant_id}`);
    }

    return rules
      .filter(gift => {
        if (!this._isGiftInValueBand(gift, projectedTotal)) return false;
        return !inCartKeys.has(this._getGiftRuleKey(gift));
      })
      .map(gift => {
        const ruleId = this._getGiftRuleId(gift);
        /** @type {Record<string, string>} */
        const properties = {
          _isGift: 'true',
          _giftVariantId: String(gift.variant_id),
          _stackGifts: 'false',
          _minCartValue:
            gift.min_cart_value != null ? String(gift.min_cart_value) : 'none',
          _maxCartValue:
            gift.max_cart_value != null ? String(gift.max_cart_value) : 'none',
        };
        if (ruleId) properties._giftRuleId = ruleId;

        return {
          id: String(gift.variant_id),
          quantity: 1,
          properties,
        };
      });
  }
};

class CartRemoveAll extends HTMLElement {
  constructor() {
    super();
    this.init();
  }

  init() {
    this.addEventListener('click', event => {
      event.preventDefault();

      // Verifica se o botão está desabilitado
      if (this.getAttribute('disabled') === 'true') {
        return; // Não executa se estiver disabled
      }

      this.handleRemoveAll();
    });
  }

  async handleRemoveAll() {
    try {
      this.disableAllRemoveButtons();
      this.showSpinner();

      const plan = await CartRemoval.buildPlan({
        itemKeys: this.dataset.itemKeys || '',
        stackGiftChecks: this.dataset.stackGiftChecks || '',
      });
      await CartRemoval.execute(plan);

      window.location.reload();
    } catch (_) {
      this.enableAllRemoveButtons();
      this.hideSpinner();
    }
  }

  showSpinner() {
    // Armazena o conteúdo original
    this.originalContent = this.innerHTML;

    // Substitui por spinner
    this.innerHTML = `
      <div class="cart-remove-all__spinner"></div>
      <span class="visually-hidden">Removendo...</span>
    `;
  }

  hideSpinner() {
    // Restaura o conteúdo original
    if (this.originalContent) {
      this.innerHTML = this.originalContent;
      this.originalContent = null;
    }
  }

  /**
   * Desabilita todos os botões cart-remove-all
   */
  disableAllRemoveButtons() {
    const allRemoveButtons = document.querySelectorAll('cart-remove-all');
    allRemoveButtons.forEach(button => {
      button.setAttribute('disabled', 'true');
      if (button instanceof HTMLElement) {
        button.style.opacity = '0.5';
        button.style.pointerEvents = 'none';
      }
    });
  }

  /**
   * Reabilita todos os botões cart-remove-all
   */
  enableAllRemoveButtons() {
    const allRemoveButtons = document.querySelectorAll('cart-remove-all');
    allRemoveButtons.forEach(button => {
      button.removeAttribute('disabled');
      if (button instanceof HTMLElement) {
        button.style.opacity = '1';
        button.style.pointerEvents = 'auto';
      }
    });
  }
}

// Define o custom element (o script também carrega com o quick add — ver snippets/scripts-quick-add.liquid)
if (!customElements.get('cart-remove-all')) {
  customElements.define('cart-remove-all', CartRemoveAll);
}
